import { generateID } from 'react-querybuilder';
import {
  InputType,
  type Query,
  type QueryBuilderFieldSchema,
  type QueryBuilderRule,
  type QueryBuilderGroupRule,
} from '@components/filter/components/common/FilterRulesBuilder/types';
import { getAutoCompletion } from '@src/api/filters';
import { LabelOption } from '../hooks';
import { DraftDimensionCondition } from '../types';

// Where a dimension's values come from, mirroring the filter builder: a remote
// autocomplete endpoint (from the filter meta value URL) or a fixed option list.
// Dimensions without a source fall back to free text.
export type DimensionValueSource =
  | { kind: 'autocomplete'; url: string; autocompleteOnly?: boolean }
  | { kind: 'select'; options: { label: string; value: string | number }[] };

// Three operators are exposed for alert-rule dimensions:
//   exists   -> has_dimension (key present, no value)
//   eq       -> dimension_equals (key = value; multi-value = IN on the key)
//   contains -> dimension_contains (key value contains substring; multi-value = OR)
const OPERATOR_OPTIONS = [
  { value: 'exists', label: 'Has any value' },
  { value: 'eq', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
];

const isEmptyValue = (value: unknown) => value === null || value === undefined || value === '';

// Per-rule validator so the builder shows an inline error when an "Equals" or
// "Contains" rule has no value. "Has any value" (exists) never requires a value.
const dimensionRuleValidator = (rule: QueryBuilderRule) => {
  if (rule.operator === 'exists') return { valid: true, reasons: [] };
  const hasValue = !!rule.value?.length && !rule.value.some((it) => isEmptyValue(it?.value));
  return hasValue
    ? { valid: true, reasons: [] }
    : { valid: false, reasons: [{ type: 'value' as const, message: 'The field is required' }] };
};

// Build react-querybuilder field schemas from the AI-alert dimension options.
// `name` is the canonical AGW key (server normalizes canonical -> raw on write),
// so persisted keys stay correct for Optimus matching. When a value source is
// resolved for a dimension (from the filter meta), the value editor supports the
// same autocomplete/select as the filter builder; otherwise it degrades to free
// text.
export const buildDimensionFields = (
  dimensionOptions: LabelOption[],
  valueSources: Record<string, DimensionValueSource> = {},
): QueryBuilderFieldSchema[] =>
  dimensionOptions.map((opt) => {
    const source = valueSources[opt.value];

    let inputType = InputType.Input;
    let valueOptions: { label: string; value: string | number }[] = [];
    let getValueOptions: QueryBuilderFieldSchema['getValueOptions'];

    if (source?.kind === 'autocomplete') {
      // SearchSelect = autocomplete-only; SearchInput = autocomplete + free input.
      inputType = source.autocompleteOnly ? InputType.SearchSelect : InputType.SearchInput;
      getValueOptions = async (pattern: string) => {
        const data = await getAutoCompletion<{ text: string; value: string }[]>(source.url, pattern)
          .then((res) => res.data)
          .catch(() => []);
        return (data || []).map((d) => ({ label: d.text, value: d.value }));
      };
    } else if (source?.kind === 'select') {
      inputType = InputType.SearchInput;
      valueOptions = source.options;
      getValueOptions = async (pattern) =>
        source.options
          .filter((option) => option.label.toLowerCase().includes(pattern.toLowerCase()))
          .map((option) => ({ ...option, value: String(option.value) }));
    }

    return {
      category: 'Dimensions',
      name: opt.value,
      label: opt.label,
      inputType,
      operatorOptions: OPERATOR_OPTIONS,
      valueOptions,
      autoCompletion: Boolean(getValueOptions),
      validator: dimensionRuleValidator as QueryBuilderFieldSchema['validator'],
      getValueOptions,
      getTagValueOptions: undefined,
      getTagKeyOptions: undefined,
    };
  });

// DraftDimensionCondition[] -> Query. Each has_dimension becomes one `exists`
// rule; dimension_equals / dimension_contains values on the same key collapse
// into one multi-value `eq` / `contains` rule (OR), so editing an existing rule
// shows the values grouped together. Equals and Contains group independently.
export const conditionsToQuery = (dims: DraftDimensionCondition[]): Query => {
  const rules: QueryBuilderRule[] = [];
  // Keyed by `${operator}|${dimensionKey}` so equals and contains on the same
  // dimension stay in separate multi-value rules.
  const valueRulesByKey = new Map<string, QueryBuilderRule>();

  dims.forEach((c) => {
    if (c.matchMode === 'dimension_equals' || c.matchMode === 'dimension_contains') {
      const operator = c.matchMode === 'dimension_contains' ? 'contains' : 'eq';
      const valueItem = {
        value: c.dimensionValue ?? '',
        label: c.dimensionLabel ?? c.dimensionValue ?? '',
      };
      const mapKey = `${operator}|${c.dimensionKey}`;
      const existing = valueRulesByKey.get(mapKey);
      if (existing) {
        existing.value.push(valueItem);
        return;
      }
      const rule: QueryBuilderRule = {
        id: generateID(),
        field: c.dimensionKey,
        operator,
        value: [valueItem],
        key: null,
      };
      valueRulesByKey.set(mapKey, rule);
      rules.push(rule);
    } else {
      rules.push({
        id: generateID(),
        field: c.dimensionKey,
        operator: 'exists',
        value: [],
        key: null,
      });
    }
  });

  return { combinator: 'and', id: generateID(), rules };
};

// Query -> DraftDimensionCondition[]. `exists` -> has_dimension; `eq` /
// `contains` emit one dimension_equals / dimension_contains row per (non-empty)
// value (multi-value = OR). OR-groups are disabled in the alert-rules builder,
// but we flatten defensively.
export const queryToConditions = (query?: Query): DraftDimensionCondition[] => {
  if (!query?.rules?.length) return [];

  const out: DraftDimensionCondition[] = [];

  const handleRule = (rule: QueryBuilderRule) => {
    if (!rule?.field) return;
    if (rule.operator === 'exists') {
      out.push({ type: 'dimension', matchMode: 'has_dimension', dimensionKey: rule.field });
      return;
    }
    const matchMode = rule.operator === 'contains' ? 'dimension_contains' : 'dimension_equals';
    (rule.value || []).forEach((v) => {
      if (isEmptyValue(v?.value)) return;
      out.push({
        type: 'dimension',
        matchMode,
        dimensionKey: rule.field,
        dimensionValue: String(v.value),
        dimensionLabel: v.label ?? String(v.value),
      });
    });
  };

  query.rules.forEach((it) => {
    const group = it as QueryBuilderGroupRule;
    if (group.rules) {
      group.rules.forEach(handleRule);
    } else {
      handleRule(it as QueryBuilderRule);
    }
  });

  return out;
};
